import { IImageLibraryDto } from "@kuika/kuika-cl-model";
import * as React from "react";
import { FilePicker } from "../../../common/file-picker";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaPopover from "../../../ui-kit/kuika-popover/kuika-popover";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import "./config-file-picker.scss";

/**
 * Choose a file from the project's library, and show which one is chosen.
 *
 * Eight settings need exactly this — the App Store connect key, the two Google Play
 * credentials, the Apple sign-in key, the Gemini key, both Firebase service accounts, the
 * push-notification certificate and the bucket credentials — and each of them had spelled
 * it out: an antd `Row`/`Col` pair, a `Popover` wrapping a `k_button-text` span, and a
 * hand-inlined 16px cross whose stroke was written as `#7A8499`. That is where a third of
 * this pane's hardcoded colour lived, and why the chosen-file chip stayed a light grey
 * pill (`#f0f2f5` on `#e6e7eb`) on the dark theme.
 *
 * The open state is the component's own. Every call site kept a `…PopupVisible` boolean in
 * its class state purely to hand it back to the popover, and none of them read it for
 * anything else.
 */

export interface IConfigFilePickerProps {
  /** The project's image library, as the pane last fetched it. */
  files: IImageLibraryDto[];
  /** `".p8"`, `".json"`, `".plist"` — what the dragger accepts. */
  acceptedFileTypes: string;
  /** Refetches the library. Runs when the picker opens, as it did on the old trigger. */
  getImageLibrary: () => void;
  /** The library row to draw as selected — an id, not a file name. */
  selectedId: string | undefined;
  /** What the chip shows. Empty means nothing is chosen and the chip is not drawn. */
  fileName?: string;
  onSelect: (image: IImageLibraryDto) => void;
  onClear: () => void;
  /** On the Choose/Replace button. Several of these are E2E hooks. */
  chooseTestId?: string;
  clearTestId?: string;
  disabled?: boolean;
}

export const ConfigFilePicker = ({
  files,
  acceptedFileTypes,
  getImageLibrary,
  selectedId,
  fileName,
  onSelect,
  onClear,
  chooseTestId,
  clearTestId,
  disabled
}: IConfigFilePickerProps) => {
  const [open, setOpen] = React.useState<boolean>(false);

  const handleOpenChange = (nextOpen: boolean): void => {
    setOpen(nextOpen);
    if (nextOpen) getImageLibrary();
  };

  return (
    <div className="kuika-config-file-picker">
      <KuikaPopover
        position="left"
        className="kuika-file-picker-popover"
        // The panel is 340px wide and these dialogs are narrow, so without a collision gap
        // Radix flushes it against the viewport edge.
        collisionPadding={16}
        visible={open}
        onVisibleChange={handleOpenChange}
        content={
          <FilePicker
            files={files}
            acceptedFileTypes={acceptedFileTypes}
            getImageLibrary={getImageLibrary}
            selectedId={selectedId}
            enableDeselect
            showFileDragger
            onChange={(_value: string, image: IImageLibraryDto): void => onSelect(image)}
            onDeselect={onClear}
          />
        }
      >
        <KuikaButton type="text" size="sm" uppercase data-testid={chooseTestId} disabled={disabled}>
          {fileName ? "Replace" : "Choose"}
        </KuikaButton>
      </KuikaPopover>

      {!!fileName && (
        <span className="kuika-config-file-picker__chip" title={fileName}>
          <span className="kuika-config-file-picker__name" data-testid="configFilePickerSelectedFile">
            {fileName}
          </span>
          <button
            type="button"
            className="kuika-config-file-picker__clear"
            aria-label={`Remove ${fileName}`}
            data-testid={clearTestId}
            onClick={onClear}
          >
            <KuikaSvg name="x" width={12} height={12} />
          </button>
        </span>
      )}
    </div>
  );
};
